/**
 * integrationsCatalog — NODE_CATALOG entries for the connected apps' action
 * nodes, and Slack's event triggers.
 *
 * Each kind shares one credential field plus per-operation params; the
 * backend ops files share an identical OPS table so adding the next
 * operation is a one-line tuple on both sides.
 *
 * Event triggers store provider/event metadata on publish. Slack events are
 * delivered to POST /api/integrations/slack/events. Other providers' inbound
 * webhooks continue to roll out.
 */
import { Box, FileText, Github, Hash, MessageSquare, Smile, User, Users } from 'lucide-react';

function f(key, label, opts = {}) {
  const { type, placeholder, help, options, teamField, ...rest } = opts;
  return {
    key,
    label,
    type: type || 'text',
    placeholder,
    help,
    options,
    teamField,
    ...rest,
  };
}

function slackChannel(key, label = 'Channel or DM', opts = {}) {
  const { placeholder, help, required = true, ...rest } = opts;
  return f(key, label, {
    type: 'slack_channel',
    placeholder: placeholder || 'Select a channel or DM',
    help,
    required,
    ...rest,
  });
}

function slackUser(key, label = 'Person', opts = {}) {
  return f(key, label, { type: 'slack_user', ...opts });
}

function slackUsers(key, label = 'People', opts = {}) {
  return f(key, label, { type: 'slack_users', ...opts });
}

function slackMessage(key, label = 'Message', opts = {}) {
  return f(key, label, { type: 'slack_message', ...opts });
}

function slackFile(key, label = 'File', opts = {}) {
  return f(key, label, { type: 'slack_file', ...opts });
}

function slackUserGroup(key, label = 'User group', opts = {}) {
  return f(key, label, { type: 'slack_usergroup', ...opts });
}

function slackEmoji(key, label = 'Reaction', opts = {}) {
  return f(key, label, { type: 'slack_emoji', ...opts });
}

function teamIntegrationStatusField(integration) {
  if (integration === 'slack') return { key: '__slack_status', label: '', type: 'slack_workspace_status' };
  if (integration === 'linear') return { key: '__linear_status', label: '', type: 'linear_workspace_status' };
  return null;
}

function credentialField(integration, kindFilter, opts = {}) {
  const label = `${integration[0].toUpperCase() + integration.slice(1)} credential`;
  return {
    key: 'credential_id',
    label: opts.optional ? `${label} (optional)` : label,
    type: 'credential',
    kindFilter,
    help: opts.help,
  };
}

function makeNode(kind, integration, displayName, description, paramFields, opts = {}) {
  // Slack + Linear: team OAuth only (Integrations page).
  const isTeamOAuth = integration === 'slack' || integration === 'linear';
  const kindFilter = [`oauth_${integration}`];
  const defaults = paramFields.reduce((acc, p) => {
    acc[p.key] = p.type === 'checkbox' ? false : (p.type === 'number' ? null : (p.type === 'lines' ? [] : ''));
    return acc;
  }, isTeamOAuth ? {} : { credential_id: null });
  const statusField = teamIntegrationStatusField(integration);
  const configSchema = [
    ...(statusField ? [statusField] : []),
    ...(isTeamOAuth ? [] : [credentialField(integration, kindFilter)]),
    ...paramFields,
  ];
  return {
    [kind]: {
      kind, displayName, category: 'integration', icon: opts.icon, provider: integration,
      description, defaults, configSchema,
    },
  };
}

function makeTrigger(kind, integration, displayName, description, eventName, paramFields = [], opts = {}) {
  const isTeamOAuth = integration === 'slack' || integration === 'linear';
  const kindFilter = [`oauth_${integration}`];
  const defaults = paramFields.reduce((acc, p) => {
    acc[p.key] = p.type === 'checkbox' ? false : (p.type === 'number' ? null : '');
    return acc;
  }, isTeamOAuth ? { event_name: eventName } : { credential_id: null, event_name: eventName });
  const statusField = teamIntegrationStatusField(integration);
  const configSchema = [
    ...(statusField ? [statusField] : []),
    ...(isTeamOAuth ? [] : [credentialField(integration, kindFilter)]),
    { key: 'event_name', label: 'Event', type: 'text', help: 'The event that starts the agent.' },
    ...paramFields,
  ];
  return {
    [kind]: {
      kind, displayName, category: 'trigger', icon: opts.icon, provider: integration,
      description, defaults, configSchema,
      triggerProvider: integration, // marks this as a per-provider event trigger
    },
  };
}

// ────────────────────────── Slack ──────────────────────────────────────────

export const SLACK_NODES = {
  // Message
  ...makeNode('slack_send_message', 'slack', 'Slack: Send message', 'Post a message to a channel or DM.',
    [slackChannel('channel'), f('text','Text', { type: 'code' }), slackMessage('thread_ts','Thread (optional)', { required: false })], { icon: MessageSquare }),
  ...makeNode('slack_update_message', 'slack', 'Slack: Update message', 'Edit a message the Trodo bot posted.',
    [slackChannel('channel'), slackMessage('ts','Message'), f('text','New text', { type: 'code' })]),
  ...makeNode('slack_delete_message', 'slack', 'Slack: Delete message', 'Delete a message the Trodo bot posted.',
    [slackChannel('channel'), slackMessage('ts','Message')]),
  ...makeNode('slack_get_message_permalink', 'slack', 'Slack: Get message permalink', 'Get a link to a message.',
    [slackChannel('channel'), slackMessage('message_ts','Message')]),
  ...makeNode('slack_search_messages', 'slack', 'Slack: Search messages', 'Search the workspace, as the person who connected Slack.',
    [f('query','Query'), slackChannel('channel','Channel or DM', { required: false, help: 'Optional. Restricts search to this conversation when set.' }), f('count','Count', { type: 'number' }), f('sort','Sort', { placeholder: 'timestamp | score' })]),
  // Channel
  ...makeNode('slack_create_channel', 'slack', 'Slack: Create channel', 'Create a public or private channel.',
    [f('name','Channel name', { placeholder: 'team-alerts' }), f('is_private','Private', { type: 'checkbox' })], { icon: Hash }),
  ...makeNode('slack_archive_channel', 'slack', 'Slack: Archive channel', 'Archive a channel the bot is in.',
    [slackChannel('channel')]),
  ...makeNode('slack_unarchive_channel', 'slack', 'Slack: Unarchive channel', 'Unarchive a channel.',
    [slackChannel('channel')]),
  ...makeNode('slack_get_channel', 'slack', 'Slack: Get channel', 'Get details of a channel or DM.',
    [slackChannel('channel')]),
  ...makeNode('slack_list_channels', 'slack', 'Slack: List channels', 'List the conversations the bot can see.',
    [f('types','Types', { placeholder: 'public_channel,private_channel,im,mpim' }), f('limit','Limit', { type: 'number' })]),
  ...makeNode('slack_channel_history', 'slack', 'Slack: Channel history', 'Read recent messages in a channel.',
    [slackChannel('channel'), f('limit','Limit', { type: 'number' })]),
  ...makeNode('slack_channel_replies', 'slack', 'Slack: Thread replies', 'Read the replies in a thread.',
    [slackChannel('channel'), slackMessage('ts','Thread'), f('limit','Limit', { type: 'number' })]),
  ...makeNode('slack_invite_to_channel', 'slack', 'Slack: Invite to channel', 'Invite people to a channel the bot is in.',
    [slackChannel('channel'), slackUsers('users','People')]),
  ...makeNode('slack_kick_from_channel', 'slack', 'Slack: Kick from channel', 'Remove a person from a channel.',
    [slackChannel('channel'), slackUser('user', 'Person', { channelField: 'channel' })]),
  ...makeNode('slack_join_channel', 'slack', 'Slack: Join channel', 'Join a public channel.',
    [slackChannel('channel')]),
  ...makeNode('slack_leave_channel', 'slack', 'Slack: Leave channel', 'Leave a channel.',
    [slackChannel('channel')]),
  ...makeNode('slack_channel_members', 'slack', 'Slack: List channel members', 'List the members of a conversation.',
    [slackChannel('channel'), f('limit','Limit', { type: 'number' })]),
  ...makeNode('slack_rename_channel', 'slack', 'Slack: Rename channel', 'Rename a channel.',
    [slackChannel('channel'), f('name','New name')]),
  ...makeNode('slack_set_channel_topic', 'slack', 'Slack: Set channel topic', 'Set a channel\'s topic.',
    [slackChannel('channel'), f('topic','Topic', { type: 'code' })]),
  ...makeNode('slack_set_channel_purpose', 'slack', 'Slack: Set channel purpose', 'Set a channel\'s purpose.',
    [slackChannel('channel'), f('purpose','Purpose', { type: 'code' })]),
  // File
  ...makeNode('slack_upload_file', 'slack', 'Slack: Upload file', 'Upload text content as a file.',
    [slackChannel('channels'), f('content','File content', { type: 'code' }),
     f('filename','Filename'), f('title','Title'), f('initial_comment','Initial comment')], { icon: FileText }),
  ...makeNode('slack_get_file', 'slack', 'Slack: Get file', 'Get details of a file.', [slackFile('file')]),
  ...makeNode('slack_list_files', 'slack', 'Slack: List files', 'List files, optionally by person or channel.',
    [slackUser('user','Person', { required: false }), slackChannel('channel','Channel or DM', { required: false }), f('types','Types filter'), f('count','Count', { type: 'number' })]),
  // Reaction
  ...makeNode('slack_add_reaction', 'slack', 'Slack: Add reaction', 'React to a message with an emoji.',
    [slackChannel('channel'), slackMessage('timestamp','Message'), slackEmoji('name')], { icon: Smile }),
  ...makeNode('slack_remove_reaction', 'slack', 'Slack: Remove reaction', 'Remove an emoji reaction.',
    [slackChannel('channel'), slackMessage('timestamp','Message'), slackEmoji('name')]),
  ...makeNode('slack_get_reactions', 'slack', 'Slack: Get reactions', 'Get the reactions on a message.',
    [slackChannel('channel'), slackMessage('timestamp','Message'), f('full','Full', { type: 'checkbox', help: 'Return the full list of users for each reaction on the selected message.' })]),
  // User
  ...makeNode('slack_get_user', 'slack', 'Slack: Get user', 'Get details of a person.', [slackUser('user')], { icon: User }),
  ...makeNode('slack_list_users', 'slack', 'Slack: List users', 'List the people in the workspace.',
    [f('limit','Limit', { type: 'number' })]),
  ...makeNode('slack_get_user_profile', 'slack', 'Slack: Get user profile', 'Get a person\'s profile.', [slackUser('user')]),
  ...makeNode('slack_get_user_status', 'slack', 'Slack: Get user status', 'Get a person\'s status.', [slackUser('user')]),
  ...makeNode('slack_update_user_profile', 'slack', 'Slack: Update user profile', 'Update the profile of the account that connected Slack.',
    [f('status_text','Status'), slackEmoji('status_emoji','Status emoji', { required: false }), f('display_name','Display name'), f('title','Title'), f('phone','Phone')]),
  // User group
  ...makeNode('slack_create_user_group', 'slack', 'Slack: Create user group', 'Create a user group (paid Slack plan).',
    [f('name','Name'), f('handle','Handle'), f('description','Description')], { icon: Users }),
  ...makeNode('slack_enable_user_group', 'slack', 'Slack: Enable user group', 'Enable a user group (paid Slack plan).', [slackUserGroup('usergroup')]),
  ...makeNode('slack_disable_user_group', 'slack', 'Slack: Disable user group', 'Disable a user group (paid Slack plan).', [slackUserGroup('usergroup')]),
  ...makeNode('slack_list_user_groups', 'slack', 'Slack: List user groups', 'List user groups (paid Slack plan).',
    [f('include_users','Include users', { type: 'checkbox' }), f('include_disabled','Include disabled', { type: 'checkbox' })]),
  ...makeNode('slack_update_user_group', 'slack', 'Slack: Update user group', 'Update a user group (paid Slack plan).',
    [slackUserGroup('usergroup'), f('name','Name'), f('handle','Handle'), f('description','Description')]),
};

/** Palette folders inside Slack, so the long action list is scannable. */
export const SLACK_ACTION_GROUPS = [
  { id: 'messages', label: 'Messages', Icon: MessageSquare },
  { id: 'channels', label: 'Channels', Icon: Hash },
  { id: 'files', label: 'Files', Icon: FileText },
  { id: 'reactions', label: 'Reactions', Icon: Smile },
  { id: 'people', label: 'People', Icon: User },
  { id: 'user_groups', label: 'User groups', Icon: Users },
];

const SLACK_KIND_GROUP = {
  slack_send_message: 'messages',
  slack_update_message: 'messages',
  slack_delete_message: 'messages',
  slack_get_message_permalink: 'messages',
  slack_search_messages: 'messages',
  slack_channel_history: 'messages',
  slack_channel_replies: 'messages',
  slack_create_channel: 'channels',
  slack_archive_channel: 'channels',
  slack_unarchive_channel: 'channels',
  slack_get_channel: 'channels',
  slack_list_channels: 'channels',
  slack_invite_to_channel: 'channels',
  slack_kick_from_channel: 'channels',
  slack_join_channel: 'channels',
  slack_leave_channel: 'channels',
  slack_channel_members: 'channels',
  slack_rename_channel: 'channels',
  slack_set_channel_topic: 'channels',
  slack_set_channel_purpose: 'channels',
  slack_upload_file: 'files',
  slack_get_file: 'files',
  slack_list_files: 'files',
  slack_add_reaction: 'reactions',
  slack_remove_reaction: 'reactions',
  slack_get_reactions: 'reactions',
  slack_get_user: 'people',
  slack_list_users: 'people',
  slack_get_user_profile: 'people',
  slack_get_user_status: 'people',
  slack_update_user_profile: 'people',
  slack_create_user_group: 'user_groups',
  slack_enable_user_group: 'user_groups',
  slack_disable_user_group: 'user_groups',
  slack_list_user_groups: 'user_groups',
  slack_update_user_group: 'user_groups',
};

for (const spec of Object.values(SLACK_NODES)) {
  spec.actionGroup = SLACK_KIND_GROUP[spec.kind];
}

export function actionGroupsFor(provider, items) {
  const defs = ACTION_GROUP_CATALOG[provider];
  if (!defs) return undefined;
  const by = {};
  for (const n of items) {
    const id = n.actionGroup;
    if (!id) continue;
    (by[id] ||= []).push(n);
  }
  return defs
    .map((g) => ({ ...g, items: by[g.id] || [] }))
    .filter((g) => g.items.length);
}

export const SLACK_TRIGGERS = {
  ...makeTrigger('slack_trigger_any_event',         'slack', 'Slack: On any event',          'Any bot event Slack sends for a channel or DM.', 'any_event',
    [slackChannel('channel','Channel or DM')]),
  ...makeTrigger('slack_trigger_bot_mention',       'slack', 'Slack: On bot app mention',    'Someone @-mentions the Trodo bot.',             'app_mention',
    [slackChannel('channel','Channel or DM')]),
  ...makeTrigger('slack_trigger_file_shared',       'slack', 'Slack: On file shared',        'A file is shared in a channel or DM.',                   'file_shared',
    [slackChannel('channel','Channel or DM')]),
  ...makeTrigger('slack_trigger_message_posted',    'slack', 'Slack: On new message in channel', 'A person posts in a channel or DM.',       'message',
    [slackChannel('channel','Channel or DM')]),
  ...makeTrigger('slack_trigger_channel_created',   'slack', 'Slack: On new public channel created', 'A public channel is created.', 'channel_created'),
  ...makeTrigger('slack_trigger_new_user',          'slack', 'Slack: On new user',           'Someone joins the workspace.',         'team_join'),
  ...makeTrigger('slack_trigger_reaction_added',    'slack', 'Slack: On reaction added',     'An emoji reaction is added.',         'reaction_added',
    [slackChannel('channel','Channel or DM'), slackEmoji('reaction','Reaction filter (optional)')]),
};

// ────────────────────────── GitHub ─────────────────────────────────────────

export const GITHUB_NODES = {
  // Issue
  ...makeNode('github_create_issue', 'github', 'GitHub: Create issue', 'Create an issue.',
    [f('repo','owner/name'), f('title','Title'), f('body','Body', { type: 'code' }), f('labels','Labels', { type: 'lines' })], { icon: Github }),
  ...makeNode('github_close_issue', 'github', 'GitHub: Close issue', 'Close an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' })]),
  ...makeNode('github_reopen_issue', 'github', 'GitHub: Reopen issue', 'Reopen an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' })]),
  ...makeNode('github_get_issue', 'github', 'GitHub: Get issue', 'Get an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' })]),
  ...makeNode('github_list_issues', 'github', 'GitHub: List issues', 'List issues in a repo.',
    [f('repo','owner/name'), f('state','State', { placeholder: 'open|closed|all' }), f('labels','Labels (comma-separated)'),
     f('per_page','Per page', { type: 'number' })]),
  ...makeNode('github_lock_issue', 'github', 'GitHub: Lock issue', 'Lock an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' }), f('lock_reason','Reason', { placeholder: 'off-topic | too heated | resolved | spam' })]),
  ...makeNode('github_unlock_issue', 'github', 'GitHub: Unlock issue', 'Unlock an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' })]),
  ...makeNode('github_comment_on_issue', 'github', 'GitHub: Comment on issue', 'Add a comment to an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' }), f('body','Body', { type: 'code' })]),
  ...makeNode('github_add_labels', 'github', 'GitHub: Add labels', 'Add labels to an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' }), f('labels','Labels', { type: 'lines' })]),
  ...makeNode('github_remove_label', 'github', 'GitHub: Remove label', 'Remove a label from an issue.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' }), f('label','Label')]),
  ...makeNode('github_assign_issue', 'github', 'GitHub: Assign issue', 'Assign an issue to a user.',
    [f('repo','owner/name'), f('issue_number','Issue number', { type: 'number' }), f('assignees','Assignees', { type: 'lines' })]),
  // PR
  ...makeNode('github_create_pr', 'github', 'GitHub: Create pull request', 'Open a pull request.',
    [f('repo','owner/name'), f('title','Title'), f('head','Head branch'), f('base','Base branch'),
     f('body','Body', { type: 'code' }), f('draft','Draft', { type: 'checkbox' })]),
  ...makeNode('github_merge_pr', 'github', 'GitHub: Merge pull request', 'Merge a pull request.',
    [f('repo','owner/name'), f('pull_number','PR number', { type: 'number' }), f('merge_method','Method', { placeholder: 'merge | squash | rebase' })]),
  ...makeNode('github_close_pr', 'github', 'GitHub: Close pull request', 'Close a pull request without merging.',
    [f('repo','owner/name'), f('pull_number','PR number', { type: 'number' })]),
  ...makeNode('github_get_pr', 'github', 'GitHub: Get pull request', 'Get a pull request.',
    [f('repo','owner/name'), f('pull_number','PR number', { type: 'number' })]),
  ...makeNode('github_list_prs', 'github', 'GitHub: List pull requests', 'List pull requests in a repo.',
    [f('repo','owner/name'), f('state','State'), f('per_page','Per page', { type: 'number' })]),
  ...makeNode('github_review_pr', 'github', 'GitHub: Review pull request', 'Submit a pull request review.',
    [f('repo','owner/name'), f('pull_number','PR number', { type: 'number' }),
     f('event','Event', { placeholder: 'APPROVE | REQUEST_CHANGES | COMMENT' }),
     f('body','Body', { type: 'code' })]),
  // File / repo
  ...makeNode('github_get_file_contents', 'github', 'GitHub: Get file contents', 'Read a file from a repo.',
    [f('repo','owner/name'), f('path','Path'), f('ref','Ref / branch')]),
  ...makeNode('github_create_or_update_file', 'github', 'GitHub: Create / update file', 'Commit a file to a repo.',
    [f('repo','owner/name'), f('path','Path'), f('content','Content', { type: 'code' }),
     f('message','Commit message'), f('branch','Branch'), f('sha','Existing sha (if update)')]),
  ...makeNode('github_delete_file', 'github', 'GitHub: Delete file', 'Delete a file with a commit.',
    [f('repo','owner/name'), f('path','Path'), f('message','Commit message'), f('branch','Branch'), f('sha','Existing sha')]),
  ...makeNode('github_get_repo', 'github', 'GitHub: Get repo', 'Get repo metadata.', [f('repo','owner/name')]),
  ...makeNode('github_list_repos', 'github', 'GitHub: List user repos', 'List repos for the authenticated user.',
    [f('per_page','Per page', { type: 'number' }), f('type','Type', { placeholder: 'all | owner | public | private | member' })]),
  ...makeNode('github_create_repo', 'github', 'GitHub: Create repo', 'Create a new repo for the authenticated user.',
    [f('name','Name'), f('description','Description'), f('private','Private', { type: 'checkbox' }), f('auto_init','Initialise', { type: 'checkbox' })]),
  // Release
  ...makeNode('github_create_release', 'github', 'GitHub: Create release', 'Create a release.',
    [f('repo','owner/name'), f('tag_name','Tag'), f('target_commitish','Target branch/commit'),
     f('name','Name'), f('body','Body', { type: 'code' }), f('draft','Draft', { type: 'checkbox' }), f('prerelease','Prerelease', { type: 'checkbox' })]),
  ...makeNode('github_get_release', 'github', 'GitHub: Get release', 'Get a release by ID.',
    [f('repo','owner/name'), f('release_id','Release id', { type: 'number' })]),
  ...makeNode('github_list_releases', 'github', 'GitHub: List releases', 'List releases for a repo.', [f('repo','owner/name')]),
  ...makeNode('github_delete_release', 'github', 'GitHub: Delete release', 'Delete a release.',
    [f('repo','owner/name'), f('release_id','Release id', { type: 'number' })]),
  // Branch / tag
  ...makeNode('github_list_branches', 'github', 'GitHub: List branches', 'List branches.', [f('repo','owner/name')]),
  ...makeNode('github_create_branch', 'github', 'GitHub: Create branch', 'Create a branch from a commit.',
    [f('repo','owner/name'), f('branch','New branch name'), f('from_sha','Base commit sha')]),
  ...makeNode('github_delete_branch', 'github', 'GitHub: Delete branch', 'Delete a branch.',
    [f('repo','owner/name'), f('branch','Branch')]),
  ...makeNode('github_list_tags', 'github', 'GitHub: List tags', 'List tags.', [f('repo','owner/name')]),
  // User / org
  ...makeNode('github_get_user', 'github', 'GitHub: Get user', 'Get a user\'s profile.', [f('username','Username')]),
  ...makeNode('github_get_authenticated_user', 'github', 'GitHub: Get authenticated user', 'Get the connected user.', []),
  ...makeNode('github_list_org_members', 'github', 'GitHub: List org members', 'List org members.',
    [f('org','Org'), f('per_page','Per page', { type: 'number' })]),
  // Workflow runs (Actions)
  ...makeNode('github_dispatch_workflow', 'github', 'GitHub: Dispatch workflow', 'Start a workflow run.',
    [f('repo','owner/name'), f('workflow_id','Workflow id or filename'), f('ref','Ref'), f('inputs','Inputs JSON', { type: 'code' })]),
  ...makeNode('github_list_workflow_runs', 'github', 'GitHub: List workflow runs', 'List recent workflow runs.',
    [f('repo','owner/name'), f('workflow_id','Workflow id or filename')]),
  ...makeNode('github_get_workflow_run', 'github', 'GitHub: Get workflow run', 'Get a workflow run.',
    [f('repo','owner/name'), f('run_id','Run id', { type: 'number' })]),
};

export const GITHUB_ACTION_GROUPS = [
  { id: 'issues', label: 'Issues' },
  { id: 'pull_requests', label: 'Pull requests' },
  { id: 'files_repos', label: 'Files & repos' },
  { id: 'releases', label: 'Releases' },
  { id: 'branches', label: 'Branches & tags' },
  { id: 'people', label: 'People' },
  { id: 'actions', label: 'Actions' },
];

const GITHUB_KIND_GROUP = {
  github_create_issue: 'issues',
  github_close_issue: 'issues',
  github_reopen_issue: 'issues',
  github_get_issue: 'issues',
  github_list_issues: 'issues',
  github_lock_issue: 'issues',
  github_unlock_issue: 'issues',
  github_comment_on_issue: 'issues',
  github_add_labels: 'issues',
  github_remove_label: 'issues',
  github_assign_issue: 'issues',
  github_create_pr: 'pull_requests',
  github_merge_pr: 'pull_requests',
  github_close_pr: 'pull_requests',
  github_get_pr: 'pull_requests',
  github_list_prs: 'pull_requests',
  github_review_pr: 'pull_requests',
  github_get_file_contents: 'files_repos',
  github_create_or_update_file: 'files_repos',
  github_delete_file: 'files_repos',
  github_get_repo: 'files_repos',
  github_list_repos: 'files_repos',
  github_create_repo: 'files_repos',
  github_create_release: 'releases',
  github_get_release: 'releases',
  github_list_releases: 'releases',
  github_delete_release: 'releases',
  github_list_branches: 'branches',
  github_create_branch: 'branches',
  github_delete_branch: 'branches',
  github_list_tags: 'branches',
  github_get_user: 'people',
  github_get_authenticated_user: 'people',
  github_list_org_members: 'people',
  github_dispatch_workflow: 'actions',
  github_list_workflow_runs: 'actions',
  github_get_workflow_run: 'actions',
};

for (const spec of Object.values(GITHUB_NODES)) {
  spec.actionGroup = GITHUB_KIND_GROUP[spec.kind];
}

const ACTION_GROUP_CATALOG = {
  slack: SLACK_ACTION_GROUPS,
  github: GITHUB_ACTION_GROUPS,
};

// ────────────────────────── Linear ─────────────────────────────────────────

const LINEAR_CREATE_ISSUE_OPTIONAL_FIELDS = [
  f('description', 'Description', { type: 'multiline' }),
  f('state_id', 'Workflow state', { type: 'linear_state', teamField: 'team_id' }),
  f('priority', 'Priority', { type: 'linear_priority' }),
  f('assignee_id', 'Assignee', { type: 'linear_assignee', teamField: 'team_id' }),
];

const LINEAR_UPDATE_ISSUE_OPTIONAL_FIELDS = [
  f('title', 'Title'),
  f('description', 'Description', { type: 'multiline' }),
  f('state_id', 'Workflow state', { type: 'linear_state', teamField: 'team_id' }),
  f('priority', 'Priority', { type: 'linear_priority' }),
  f('assignee_id', 'Assignee', { type: 'linear_assignee', teamField: 'team_id' }),
];

const LINEAR_SUB_ISSUE_OPTIONAL_FIELDS = [
  f('description', 'Description', { type: 'multiline' }),
  f('state_id', 'Workflow state', { type: 'linear_state', teamField: 'team_id' }),
  f('priority', 'Priority', { type: 'linear_priority' }),
  f('assignee_id', 'Assignee', { type: 'linear_assignee', teamField: 'team_id' }),
];

export const LINEAR_NODES = {
  linear_create_issue: {
    kind: 'linear_create_issue',
    displayName: 'Linear: Create issue',
    category: 'integration',
    icon: Box,
    provider: 'linear',
    description: 'Create an issue.',
    defaults: {
      team_id: '',
      title: '',
      description: '',
      state_id: '',
      priority: null,
      assignee_id: null,
      sub_issues: [],
      extra_fields: [],
    },
    configSchema: [
      teamIntegrationStatusField('linear'),
      f('team_id', 'Linear team', { type: 'linear_team' }),
      f('title', 'Title'),
      {
        key: '_additional_fields',
        label: 'Additional Fields',
        type: 'additional_fields',
        storageKey: 'extra_fields',
        fields: LINEAR_CREATE_ISSUE_OPTIONAL_FIELDS,
      },
      f('sub_issues', 'Sub-issues (optional)', {
        type: 'linear_sub_issues',
        teamField: 'team_id',
        optionalFields: LINEAR_SUB_ISSUE_OPTIONAL_FIELDS,
      }),
    ],
  },
  linear_update_issue: {
    kind: 'linear_update_issue',
    displayName: 'Linear: Update issue',
    category: 'integration',
    icon: Box,
    provider: 'linear',
    description: 'Update an issue.',
    defaults: {
      issue_id: '',
      title: '',
      description: '',
      team_id: '',
      state_id: '',
      priority: null,
      assignee_id: null,
      update_fields: [],
      sub_issues: [],
    },
    configSchema: [
      teamIntegrationStatusField('linear'),
      f('team_id', 'Linear team', { type: 'linear_team' }),
      f('issue_id', 'Issue id'),
      {
        key: '_update_fields',
        label: 'Update Fields',
        type: 'additional_fields',
        storageKey: 'update_fields',
        fields: LINEAR_UPDATE_ISSUE_OPTIONAL_FIELDS,
      },
      f('sub_issues', 'Sub-issues (optional)', {
        type: 'linear_sub_issues',
        mode: 'update',
        teamField: 'team_id',
        optionalFields: LINEAR_UPDATE_ISSUE_OPTIONAL_FIELDS,
      }),
    ],
  },
  linear_get_issue: {
    kind: 'linear_get_issue',
    displayName: 'Linear: Get issue',
    category: 'integration',
    icon: Box,
    provider: 'linear',
    description: 'Get an issue.',
    defaults: {
      issue_id: '',
      include_sub_issues: false,
    },
    configSchema: [
      teamIntegrationStatusField('linear'),
      f('issue_id', 'Issue id (or identifier like ENG-123)'),
      f('include_sub_issues', 'Include sub-issues', { type: 'checkbox' }),
    ],
  },
  ...makeNode('linear_archive_issue', 'linear', 'Linear: Archive issue', 'Archive an issue.', [f('issue_id','Issue id')]),
  linear_search_issues: {
    kind: 'linear_search_issues',
    displayName: 'Linear: Search issues',
    category: 'integration',
    icon: Box,
    provider: 'linear',
    description: 'Search issues.',
    defaults: {
      query: '',
      first: null,
      include_sub_issues: false,
    },
    configSchema: [
      teamIntegrationStatusField('linear'),
      f('query', 'Query'),
      f('first', 'Limit', { type: 'number' }),
      f('include_sub_issues', 'Include sub-issues', { type: 'checkbox' }),
    ],
  },
  ...makeNode('linear_create_comment', 'linear', 'Linear: Comment', 'Add a comment to an issue.',
    [f('issue_id','Issue id'), f('body','Body', { type: 'multiline' })]),
  ...makeNode('linear_list_teams', 'linear', 'Linear: List teams', 'List teams.', []),
  ...makeNode('linear_list_states', 'linear', 'Linear: List workflow states', 'List states for a team.',
    [f('team_id', 'Linear team', { type: 'linear_team' })]),
};

/** Providers a person can pick from the palette. */
export const PALETTE_PROVIDERS = ['slack', 'github', 'linear'];

/** Every integration node the editor knows. Only Slack has inbound events. */
export const ALL_INTEGRATION_NODES = {
  ...SLACK_NODES, ...SLACK_TRIGGERS,
  ...GITHUB_NODES,
  ...LINEAR_NODES,
};
